import { Button } from "@/components/ui/button";
import { useForm } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { updateProfileSchema } from "@/features/user/api/update-profile";
import { AvatarDropzone } from "@/features/user/components/avatar-dropzone";
import type { FileEntity } from "@/hooks/use-upload";
import { queries } from "@/utils/query";
import { cn } from "@/utils/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ImageIcon, Undo2Icon } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

export const Route = createFileRoute("/user/settings/profile")({
  head: () => ({
    meta: [
      {
        title: "个人资料",
      },
    ],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const { data: currentUser } = queries.auth.getCurrentSession.useSuspenseQuery(
    {
      select: (data) => {
        if (data.session) {
          return data.user;
        }
        throw new Error("Session not found");
      },
    },
  );

  const updateAvatarMutation = queries.user.updateAvatar.useMutation();
  const [avatarFileEntity, setAvatarFileEntity] = useState<FileEntity>();

  const updateProfileMutation = queries.user.updateProfile.useMutation();

  const form = useForm(updateProfileSchema, {
    defaultValues: {
      username: currentUser.username,
      nickname: currentUser.nickname,
      bio: currentUser.bio,
    },
    onSubmit: async ({ value }) => {
      await Promise.all([
        updateProfileMutation.mutateAsync(value),
        avatarFileEntity
          ? updateAvatarMutation.mutateAsync({ avatar: avatarFileEntity })
          : Promise.resolve(),
      ])
        .then(() => {
          toast.success("个人资料已更新，如未生效请尝试刷新页面");
        })
        .finally(() => {
          queryClient.invalidateQueries({ queryKey: ["current-user"] });
        });
    },
  });

  return (
    <>
      <div className="relative isolate aspect-toutu">
        <img
          src="https://picsum.photos/800/450"
          alt=""
          className="object-cover size-full -z-10 absolute inset-0"
        />
        <div className="absolute inset-0 bg-gray-subtle-bg/80" />
        <div className="absolute inset-0 flex flex-col gap-2 items-center justify-center">
          <ImageIcon className="size-8" />
          <span>修改我的头图</span>
        </div>
      </div>

      <div className="relative">
        <Button
          intent="link"
          flavor="accent"
          size="md"
          className="absolute right-6 top-6"
          asChild
        >
          <Link to="/user/$username" params={{ username: currentUser.userId }}>
            返回我的主页
            <Undo2Icon />
          </Link>
        </Button>

        <div>
          <div className="flex items-start gap-6">
            <AvatarDropzone
              className="size-50"
              defaultPreviewUrl={currentUser.avatarUrl}
              onCompleted={(fileEntity) => setAvatarFileEntity(fileEntity)}
            />

            <div className="flex-1 mt-25">
              <form.Root className="space-y-6">
                <form.Field
                  name="username"
                  render={(field) => (
                    <field.Container
                      label="用户名："
                      className="flex items-center gap-4 [&>label]:w-24"
                      showLabel
                      detail="本月还能修改 0 次"
                    >
                      <Input
                        className={cn(
                          "h-9 max-w-[30ch]",
                          field.getMeta().isPristine &&
                            "text-gray-fg-secondary",
                        )}
                        placeholder="用户名"
                      />
                    </field.Container>
                  )}
                />

                <Separator />

                <form.Field
                  name="nickname"
                  render={(field) => (
                    <field.Container
                      label="昵称："
                      className="flex items-center gap-4 [&>label]:w-24"
                      showLabel
                    >
                      <Input
                        className={cn(
                          "h-9 max-w-[30ch]",
                          field.getMeta().isPristine &&
                            "text-gray-fg-secondary",
                        )}
                        placeholder="昵称"
                      />
                    </field.Container>
                  )}
                />

                <Separator />

                <form.Field
                  name="bio"
                  render={(field) => (
                    <field.Container
                      label="个性签名："
                      className="flex items-center gap-4 [&>label]:w-24"
                      showLabel
                      detail="还可以输入 30 个字"
                    >
                      <Input
                        className={cn(
                          "h-9 flex-1",
                          field.getMeta().isPristine &&
                            "text-gray-fg-secondary",
                        )}
                        placeholder="个性签名"
                      />
                    </field.Container>
                  )}
                />

                <div className="mt-18 space-x-4">
                  <form.Subscribe
                    selector={(formState) => [formState.isSubmitting]}
                  >
                    {([isSubmitting]) => (
                      <Button
                        type="reset"
                        disabled={isSubmitting}
                        intent="soft"
                        flavor="gray"
                        onClick={() => form.reset()}
                      >
                        还原全部
                      </Button>
                    )}
                  </form.Subscribe>

                  <form.Submit>保存</form.Submit>
                </div>
              </form.Root>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
